(() => {
  const componentSelector = 'results-list[data-curated-collection-handle]';
  const safeHandlePattern = /^[a-z0-9][a-z0-9-]*$/;
  const activeRequests = new WeakSet();

  function safelyDecode(value) {
    let decoded = String(value || '').trim();

    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const next = decodeURIComponent(decoded);
        if (next === decoded) break;
        decoded = next;
      } catch {
        break;
      }
    }

    return decoded;
  }

  function parseProductReference(value) {
    const decoded = safelyDecode(value);
    let handle = '';
    let variant = '';

    try {
      const productUrl = new URL(decoded, window.location.origin);
      const match = productUrl.pathname.match(/\/products\/([^/?#]+)/i);

      if (match) {
        handle = safelyDecode(match[1]).toLowerCase();
        variant = productUrl.searchParams.get('variant') || '';
      } else if (!decoded.includes('/') && !decoded.includes('?')) {
        handle = decoded.toLowerCase();
      }
    } catch {
      handle = decoded.split('?')[0].split('/').filter(Boolean).pop()?.toLowerCase() || '';
    }

    if (!safeHandlePattern.test(handle)) return null;
    return { handle, variant: /^\d+$/.test(variant) ? variant : '' };
  }

  function getConfiguration(component) {
    const params = new URLSearchParams(window.location.search);
    const productReference = parseProductReference(params.get('first'));
    if (!productReference) return null;

    const topLevelVariant = params.get('variant') || '';
    const variant = /^\d+$/.test(topLevelVariant) ? topLevelVariant : productReference.variant;
    const sourceValue = (params.get('source') || component.dataset.curatedCollectionHandle || '').toLowerCase();
    const source = safeHandlePattern.test(sourceValue) ? sourceValue : component.dataset.curatedCollectionHandle;
    const requestedLimit = Number.parseInt(params.get('limit') || '', 10);
    const limit = Number.isFinite(requestedLimit) ? Math.min(Math.max(requestedLimit, 1), 50) : 16;

    return {
      handle: productReference.handle,
      variant,
      source,
      currentCollection: component.dataset.curatedCollectionHandle,
      limit,
    };
  }

  function addPreviewParameters(url) {
    const currentParams = new URLSearchParams(window.location.search);

    for (const key of ['preview_theme_id', '_fd', 'pb']) {
      const value = currentParams.get(key);
      if (value) url.searchParams.set(key, value);
    }

    return url;
  }

  async function fetchHtml(url) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });

    if (!response.ok) throw new Error(`Request failed with status ${response.status}`);
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }

  function productIdForCard(card) {
    return card?.dataset?.productId || card?.querySelector('[data-product-id]')?.dataset?.productId || '';
  }

  function productHandleForCard(card) {
    const link = card?.querySelector('a[href*="/products/"]');
    if (!(link instanceof HTMLAnchorElement)) return '';

    try {
      return new URL(link.href, window.location.origin).pathname.match(/\/products\/([^/?#]+)/i)?.[1]?.toLowerCase() || '';
    } catch {
      return '';
    }
  }

  function forceVariantOnLinks(card, variant) {
    if (!variant) return;

    for (const link of card.querySelectorAll('a[href*="/products/"]')) {
      if (!(link instanceof HTMLAnchorElement)) continue;

      try {
        const url = new URL(link.href, window.location.origin);
        // The variant identifies the advertised color. Product-page logic still requires size selection.
        url.searchParams.set('variant', variant);
        link.href = `${url.pathname}${url.search}${url.hash}`;
      } catch {
        // Leave malformed links unchanged.
      }
    }
  }

  async function fetchCuratedCard(configuration) {
    const url = addPreviewParameters(
      new URL(`/products/${encodeURIComponent(configuration.handle)}`, window.location.origin)
    );
    url.searchParams.set('view', 'card');
    if (configuration.variant) url.searchParams.set('variant', configuration.variant);

    const document = await fetchHtml(url);
    const template = document.querySelector('template[data-curated-product-card]');
    const card = template?.content?.querySelector('li.product-grid__item')?.cloneNode(true);

    if (!(card instanceof HTMLLIElement)) throw new Error('The curated product card could not be rendered');
    forceVariantOnLinks(card, configuration.variant);
    return card;
  }

  async function fetchSourceCards(source) {
    const url = addPreviewParameters(new URL(`/collections/${encodeURIComponent(source)}`, window.location.origin));
    const document = await fetchHtml(url);
    return [...document.querySelectorAll('#ResultsList .product-grid > li.product-grid__item')];
  }

  function configureViewAll(component) {
    const actions = component.querySelector('[data-curated-actions]');
    const link = actions?.querySelector('[data-curated-view-all]');

    if (!(actions instanceof HTMLElement) || !(link instanceof HTMLAnchorElement)) return;

    const url = new URL(window.location.href);
    for (const key of ['first', 'variant', 'source', 'limit']) {
      url.searchParams.delete(key);
    }
    url.hash = '';

    link.href = `${url.pathname}${url.search}`;
    actions.hidden = false;
  }

  function normalizeCard(card, index) {
    for (const className of [...card.classList]) {
      if (/^product-grid__item--\d+$/.test(className)) card.classList.remove(className);
    }

    card.classList.add(`product-grid__item--${index}`);
    card.dataset.page = '1';
    card.setAttribute('ref', 'cards[]');
  }

  async function curate(component) {
    const configuration = getConfiguration(component);
    if (!configuration || activeRequests.has(component)) return;

    const grid = component.querySelector('#ResultsList .product-grid');
    if (!(grid instanceof HTMLUListElement) || grid.dataset.curatedApplied === 'true') return;

    activeRequests.add(component);

    try {
      const visibleCards = [...grid.querySelectorAll(':scope > li.product-grid__item')];
      let sourceCards = visibleCards;

      if (configuration.source && configuration.source !== configuration.currentCollection) {
        sourceCards = await fetchSourceCards(configuration.source);
      }

      let pinnedCard = sourceCards.find((card) => productHandleForCard(card) === configuration.handle) || null;

      if (configuration.variant || !pinnedCard) {
        pinnedCard = await fetchCuratedCard(configuration);
      }

      if (!(pinnedCard instanceof HTMLLIElement)) return;

      const pinnedProductId = productIdForCard(pinnedCard);
      const remainingCards = sourceCards.filter((card) => {
        if (card === pinnedCard) return false;
        if (productHandleForCard(card) === configuration.handle) return false;
        return !pinnedProductId || productIdForCard(card) !== pinnedProductId;
      });

      const total = configuration.limit || 16;
      const orderedCards = [pinnedCard, ...remainingCards].slice(0, total);

      orderedCards.forEach(normalizeCard);
      grid.dataset.curatedApplied = 'true';
      grid.replaceChildren(...orderedCards);
      component.dataset.curatedActive = 'true';

      // Server pagination no longer describes the curated result set accurately.
      component.querySelector('.pagination-wrapper')?.remove();
      configureViewAll(component);
    } catch (error) {
      console.warn('[Curated collection]', error);
    } finally {
      activeRequests.delete(component);
    }
  }

  function initialize() {
    for (const component of document.querySelectorAll(componentSelector)) {
      curate(component);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  new MutationObserver(initialize).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();
